<template>
  <div class="admin-dashboard">
    <h1 class="section-title">{{ $t('admin.title') }}</h1>

    <div class="admin-tabs">
      <button 
        class="tab-btn"
        :class="{ active: activeTab === 'books' }"
        @click="activeTab = 'books'"
      >
        <i class="pi pi-book"></i> {{ $t('admin.books') }}
      </button>
      <button 
        class="tab-btn"
        :class="{ active: activeTab === 'categories' }"
        @click="activeTab = 'categories'"
      >
        <i class="pi pi-list"></i> {{ $t('admin.categories') }}
      </button>
      <button 
        class="tab-btn"
        :class="{ active: activeTab === 'advertisements' }"
        @click="activeTab = 'advertisements'"
      >
        <i class="pi pi-image"></i> {{ $t('admin.advertisements') }}
      </button>
    </div>

    <!-- Books Management -->
    <div v-if="activeTab === 'books'" class="tab-content">
      <div class="section-header">
        <h2>{{ $t('admin.booksManagement') }}</h2>
        <button class="btn btn-primary" @click="showAddBookModal = true">
          <i class="pi pi-plus"></i> {{ $t('admin.addNewBook') }}
        </button>
      </div>

      <div class="books-list">
        <div v-if="loading" class="loading">
          <div class="spinner"></div>
          <p>{{ $t('admin.loadingBooks') }}</p>
        </div>
        
        <div v-else-if="error" class="error-message">
          {{ error }}
        </div>

        <table v-else class="admin-table">
          <thead>
            <tr>
              <th>{{ $t('admin.title') }}</th>
              <th>{{ $t('admin.categories') }}</th>
              <th>{{ $t('admin.status') }}</th>
              <th>{{ $t('admin.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="book in books" :key="book.id">
              <td>{{ book.title }}</td>
              <td>
                <span v-for="category in book.categories" 
                      :key="category.id" 
                      class="category-tag">
                  {{ category.name }}
                </span>
              </td>
              <td>
                <span :class="['status-badge', book.is_published ? 'published' : 'draft']">
                  {{ book.is_published ? $t('admin.published') : $t('admin.draft') }}
                </span>
              </td>
              <td class="actions">
                <button class="icon-btn" @click="editBook(book)">
                  <i class="pi pi-pencil"></i>
                </button>
                <button class="icon-btn delete" @click="deleteBook(book.id)">
                  <i class="pi pi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Categories Management -->
    <div v-if="activeTab === 'categories'" class="tab-content">
      <div class="section-header">
        <h2>{{ $t('admin.categoriesManagement') }}</h2>
        <button class="btn btn-primary" @click="showAddCategoryModal = true">
          <i class="pi pi-plus"></i> {{ $t('admin.addNewCategory') }}
        </button>
      </div>

      <div class="categories-list">
        <div v-if="loading" class="loading">
          <div class="spinner"></div>
          <p>{{ $t('admin.loadingCategories') }}</p>
        </div>
        
        <div v-else-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-else>
          <p>Debug - Categories count: {{ categories.length }}</p>
          <table class="admin-table">
            <thead>
              <tr>
                <th>{{ $t('admin.name') }}</th>
                <th>{{ $t('admin.slug') }}</th>
                <th>{{ $t('admin.booksCount') }}</th>
                <th>{{ $t('admin.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="category in categories" :key="category.id">
                <td>{{ category.name }}</td>
                <td>{{ category.slug }}</td>
                <td>{{ category.books_count || 0 }}</td>
                <td class="actions">
                  <button class="icon-btn" @click="editCategory(category)">
                    <i class="pi pi-pencil"></i>
                  </button>
                  <button class="icon-btn delete" @click="deleteCategory(category.id)">
                    <i class="pi pi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Advertisements Management -->
    <div v-if="activeTab === 'advertisements'" class="tab-content">
      <div class="section-header">
        <h2>{{ $t('admin.advertisementsManagement') }}</h2>
        <button class="btn btn-primary" @click="openAdModal()">
          <i class="pi pi-plus"></i> {{ $t('admin.addNewAdvertisement') }}
        </button>
      </div>
      <div class="ads-list">
        <div v-if="adLoading" class="loading"><div class="spinner"></div>{{ $t('admin.loadingAdvertisements') }}</div>
        <div v-else-if="adError" class="error-message">{{ adError }}</div>
        <table v-else class="admin-table">
          <thead>
            <tr>
              <th>{{ $t('advertisements.image') }}</th>
              <th>{{ $t('advertisements.name') }}</th>
              <th>{{ $t('advertisements.description') }}</th>
              <th>{{ $t('advertisements.book') }}</th>
              <th>{{ $t('advertisements.isUrl') }}</th>
              <th>{{ $t('advertisements.url') }}</th>
              <th>{{ $t('advertisements.sortOrder') }}</th>
              <th>{{ $t('advertisements.isPublished') }}</th>
              <th>{{ $t('admin.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ad in validAdvertisements" :key="ad.id">
              <td><img :src="ad.image" :alt="ad.name" style="width:50px;height:80px;object-fit:cover;"></td>
              <td>{{ ad.name }}</td>
              <td>{{ ad.description }}</td>
              <td>{{ ad.book_title || '-' }}</td>
              <td><input type="checkbox" v-model="ad.is_url" @change="updateAdField(ad, 'is_url', ad.is_url)"></td>
              <td><a v-if="ad.url" :href="ad.url" target="_blank">{{ ad.url }}</a></td>
              <td><input type="number" v-model.number="ad.sort_id" @change="updateAdField(ad, 'sort_id', ad.sort_id)" style="width:60px;"></td>
              <td><input type="checkbox" v-model="ad.is_active" @change="updateAdField(ad, 'is_active', ad.is_active)"></td>
              <td>
                <button class="icon-btn" @click="openAdModal(ad)"><i class="pi pi-pencil"></i></button>
                <button class="icon-btn delete" @click="deleteAd(ad)"><i class="pi pi-trash"></i></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Add/Edit Advertisement Modal -->
      <div v-if="showAddAdModal" class="modal-overlay">
        <div class="modal-content">
          <h3>{{ editingAd ? $t('admin.editAdvertisement') : $t('admin.addNewAdvertisement') }}</h3>
          <div v-if="adError" class="error-message">{{ adError }}</div>
          <form @submit.prevent="saveAd" class="ad-form">
            <div class="form-group">
              <label>{{ $t('advertisements.name') }} *</label>
              <input type="text" v-model="adForm.name" required />
            </div>
            <div class="form-group">
              <label>{{ $t('advertisements.description') }}</label>
              <textarea v-model="adForm.description" rows="2"></textarea>
            </div>
            <div class="form-group">
              <label>{{ $t('advertisements.image') }} *</label>
              <input type="file" @change="handleAdImageUpload" accept="image/*" />
              <div v-if="adForm.existing_image_url" class="existing-file">
                <img :src="adForm.existing_image_url" :alt="$t('admin.currentFile')" style="width:60px;height:90px;object-fit:cover;" />
              </div>
            </div>
            <div class="form-group">
              <label>{{ $t('advertisements.book') }}</label>
              <select v-model="adForm.book">
                <option :value="null">-- {{ $t('advertisements.noBookSelected') }} --</option>
                <option v-for="book in adBooks" :key="book.id" :value="book.id">{{ book.title }}</option>
              </select>
            </div>
            <div class="form-group">
              <label><input type="checkbox" v-model="adForm.is_url" /> {{ $t('advertisements.isUrl') }}</label>
            </div>
            <div class="form-group" v-if="adForm.is_url">
              <label>{{ $t('advertisements.url') }} *</label>
              <input type="url" v-model="adForm.url" required />
            </div>
            <div class="form-group">
              <label>{{ $t('advertisements.sortOrder') }}</label>
              <input type="number" v-model.number="adForm.sort_id" />
            </div>
            <div class="form-group">
              <label><input type="checkbox" v-model="adForm.is_active" /> {{ $t('advertisements.isPublished') }}</label>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="showAddAdModal = false">{{ $t('admin.cancel') }}</button>
              <button type="submit" class="btn btn-primary" :disabled="adSaving">{{ adSaving ? $t('admin.saving') : $t('admin.saveAdvertisement') }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Add/Edit Book Modal -->
    <div v-if="showAddBookModal" class="modal-overlay">
      <div class="modal-content">
        <h3>{{ editingBook ? $t('admin.editBook') : $t('admin.addNewBook') }}</h3>
        
        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <form @submit.prevent="saveBook" class="book-form">
          <div class="form-group">
            <label for="title">{{ $t('admin.title') }} *</label>
            <input 
              type="text" 
              id="title" 
              v-model="bookForm.title" 
              required
            />
          </div>

          <div class="form-group">
            <label for="description">{{ $t('admin.description') }} *</label>
            <textarea 
              id="description" 
              v-model="bookForm.description"
              rows="4"
              required
            ></textarea>
          </div>

          <div class="form-group">
            <label for="publication_date">{{ $t('admin.publicationDate') }} *</label>
            <input 
              type="date" 
              id="publication_date" 
              v-model="bookForm.publication_date"
              required
            />
          </div>

          <div class="form-group">
            <label for="isbn">{{ $t('admin.isbn') }}</label>
            <input 
              type="text" 
              id="isbn" 
              v-model="bookForm.isbn"
            />
          </div>

          <div class="form-group">
            <label for="categories">{{ $t('admin.categories') }}</label>
            <select 
              id="categories" 
              v-model="bookForm.categories" 
              multiple
            >
              <option v-for="category in categories" 
                      :key="category.id" 
                      :value="category.id">
                {{ category.name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="pdf">{{ $t('admin.pdfFile') }} {{ !editingBook ? '*' : '' }}</label>
            <input 
              type="file" 
              id="pdf" 
              @change="handleFileUpload"
              accept=".pdf"
              :required="!editingBook"
            />
            <div v-if="editingBook && bookForm.existing_pdf_url" class="existing-file">
              <p><strong>{{ $t('admin.currentFile') }}:</strong></p>
              <p class="file-info">
                <span class="filename">{{ getPdfFilename(bookForm.existing_pdf_url) }}</span>
                <a :href="bookForm.existing_pdf_url" target="_blank" class="file-link">
                  {{ $t('admin.viewCurrentPdf') }}
                </a>
              </p>
            </div>
          </div>

          <div class="form-group">
            <label for="cover">{{ $t('admin.coverImage') }}</label>
            <input 
              type="file" 
              id="cover" 
              @change="handleCoverUpload"
              accept="image/*"
            />
            <div v-if="editingBook && bookForm.existing_cover_url" class="existing-file">
              <p><strong>{{ $t('admin.currentFile') }}:</strong></p>
              <img :src="bookForm.existing_cover_url" :alt="bookForm.title" class="existing-cover" />
            </div>
          </div>

          <div class="form-group checkbox">
            <label>
              <input 
                type="checkbox" 
                v-model="bookForm.is_published"
              />
              {{ $t('admin.publishImmediately') }}
            </label>
          </div>

          <div class="form-group checkbox">
            <label>
              <input 
                type="checkbox" 
                v-model="bookForm.is_featured"
              />
              {{ $t('admin.featureThisBook') }}
            </label>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="showAddBookModal = false">
              {{ $t('admin.cancel') }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? $t('admin.saving') : $t('admin.saveBook') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add/Edit Category Modal -->
    <div v-if="showAddCategoryModal" class="modal-overlay">
      <div class="modal-content">
        <h3>{{ editingCategory ? $t('admin.editCategory') : $t('admin.addNewCategory') }}</h3>
        
        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <form @submit.prevent="saveCategory" class="category-form">
          <div class="form-group">
            <label for="name">{{ $t('admin.name') }} *</label>
            <input 
              type="text" 
              id="name" 
              v-model="categoryForm.name" 
              required
            />
          </div>

          <div class="form-group">
            <label for="description">{{ $t('admin.description') }}</label>
            <textarea 
              id="description" 
              v-model="categoryForm.description"
              rows="4"
            ></textarea>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="showAddCategoryModal = false">
              {{ $t('admin.cancel') }}
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? $t('admin.saving') : $t('admin.saveCategory') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/services/api';

export default defineComponent({
  name: 'AdminDashboardView',
  setup() {
    const { t } = useI18n();
    const activeTab = ref('books');
    const loading = ref(false);
    const error = ref(null);
    const saving = ref(false);

    // Books state
    const books = ref([]);
    const showAddBookModal = ref(false);
    const editingBook = ref(null);
    const bookForm = ref({
      title: '',
      description: '',
      categories: [],
      authors: [],
      publication_date: new Date().toISOString().split('T')[0], // Default to today
      isbn: '',
      is_published: false,
      is_featured: false,
      pdf_file: null,
      cover_image: null
    });

    // Categories state
    const categories = ref([]);
    const showAddCategoryModal = ref(false);
    const editingCategory = ref(null);
    const categoryForm = ref({
      name: '',
      description: ''
    });

    // --- Advertisement State ---
    const advertisements = ref([]);
    const showAddAdModal = ref(false);
    const editingAd = ref(null);
    const adForm = ref({
      name: '',
      description: '',
      image: null,
      book: null,
      is_url: false,
      url: '',
      sort_id: 0,
      is_active: true,
      existing_image_url: null,
    });
    const adLoading = ref(false);
    const adError = ref(null);
    const adSaving = ref(false);
    const adBooks = books; // Use books for book selection

    // Computed property for valid advertisements
    const validAdvertisements = computed(() => {
      return advertisements.value.filter(ad => ad && ad.id);
    });

    // Fetch data
    const fetchBooks = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const response = await api.getBooks({ include_drafts: true });
        books.value = response.data.results;
      } catch (err) {
        error.value = t('admin.failedToFetchBooks');
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const fetchCategories = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const response = await api.getCategories();
        console.log('Categories response:', response);
        if (!response.data) {
          throw new Error('No data received from categories API');
        }
        categories.value = Array.isArray(response.data.results) ? response.data.results : [];
        console.log('Categories state after update:', categories.value);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        console.error('Error response:', err.response);
        error.value = t('admin.failedToFetchCategories');
      } finally {
        loading.value = false;
      }
    };

    // Fetch advertisements
    const fetchAdvertisements = async () => {
      adLoading.value = true;
      adError.value = null;
      try {
        const res = await api.getAdvertisements();
        console.log('Advertisements response:', res);
        console.log('Advertisements data:', res.data);
        
        // Ensure we have valid data
        if (res.data && Array.isArray(res.data)) {
          advertisements.value = res.data.filter(ad => ad && ad.id);
        } else if (res.data && res.data.results && Array.isArray(res.data.results)) {
          advertisements.value = res.data.results.filter(ad => ad && ad.id);
        } else {
          advertisements.value = [];
        }
        
        console.log('Filtered advertisements:', advertisements.value);
      } catch (e) {
        adError.value = t('admin.failedToFetchAdvertisements');
        console.error(e);
      } finally {
        adLoading.value = false;
      }
    };

    // Open modal for add/edit
    const openAdModal = (ad = null) => {
      editingAd.value = ad;
      if (ad) {
        adForm.value = {
          name: ad.name,
          description: ad.description,
          image: null,
          book: ad.book,
          is_url: ad.is_url,
          url: ad.url,
          sort_id: ad.sort_id,
          is_active: ad.is_active,
          existing_image_url: ad.image,
        };
      } else {
        adForm.value = {
          name: '',
          description: '',
          image: null,
          book: null,
          is_url: false,
          url: '',
          sort_id: 0,
          is_active: true,
          existing_image_url: null,
        };
      }
      showAddAdModal.value = true;
    };

    // Handle image upload
    const handleAdImageUpload = (event) => {
      adForm.value.image = event.target.files[0];
    };

    // Save (create or update) advertisement
    const saveAd = async () => {
      adSaving.value = true;
      adError.value = null;
      try {
        const formData = new FormData();
        formData.append('name', adForm.value.name);
        formData.append('description', adForm.value.description);
        formData.append('is_url', adForm.value.is_url);
        formData.append('url', adForm.value.url);
        formData.append('sort_id', adForm.value.sort_id);
        formData.append('is_active', adForm.value.is_active);
        if (adForm.value.book) formData.append('book', adForm.value.book);
        if (adForm.value.image) formData.append('image', adForm.value.image);
        if (editingAd.value) {
          await api.updateAdvertisement(editingAd.value.id, formData);
        } else {
          await api.createAdvertisement(formData);
        }
        showAddAdModal.value = false;
        await fetchAdvertisements();
      } catch (e) {
        adError.value = t('admin.failedToSaveAdvertisement');
        console.error(e);
      } finally {
        adSaving.value = false;
      }
    };

    // Delete advertisement
    const deleteAd = async (ad) => {
      if (!ad || !ad.id) {
        console.error('Invalid advertisement object:', ad);
        adError.value = t('advertisements.errorLoadingAdvertisements');
        return;
      }
      
      if (!confirm(t('admin.confirmDeleteAdvertisement'))) return;
      try {
        await api.deleteAdvertisement(ad.id);
        await fetchAdvertisements();
      } catch (e) {
        adError.value = t('admin.failedToDeleteAdvertisement');
        console.error(e);
      }
    };

    // Inline update for sort_id and is_active
    const updateAdField = async (ad, field, value) => {
      if (!ad || !ad.id) {
        console.error('Invalid advertisement object:', ad);
        adError.value = t('advertisements.errorLoadingAdvertisements');
        return;
      }
      
      try {
        await api.updateAdvertisement(ad.id, { [field]: value });
        await fetchAdvertisements();
      } catch (e) {
        adError.value = t('admin.failedToSaveAdvertisement');
        console.error(e);
      }
    };

    // Book actions
    const handleFileUpload = (event) => {
      bookForm.value.pdf_file = event.target.files[0];
    };

    const handleCoverUpload = (event) => {
      bookForm.value.cover_image = event.target.files[0];
    };

    const getPdfFilename = (url) => {
      if (!url) return '';
      // Extract filename from URL
      const urlParts = url.split('/');
      const filename = urlParts[urlParts.length - 1];
      // Remove query parameters if any
      return filename.split('?')[0];
    };

    const editBook = (book) => {
      editingBook.value = book;
      bookForm.value = {
        title: book.title,
        description: book.description,
        categories: book.categories.map(c => c.id),
        authors: book.authors.map(a => a.id),
        publication_date: book.publication_date,
        isbn: book.isbn || '',
        is_published: Boolean(book.is_published),
        is_featured: Boolean(book.is_featured),
        pdf_file: null,
        cover_image: null,
        existing_pdf_url: book.pdf_file || null,
        existing_cover_url: book.cover_image || null
      };
      showAddBookModal.value = true;
    };

    const saveBook = async () => {
      saving.value = true;
      error.value = null;
      
      try {
        if (!bookForm.value.title || !bookForm.value.description || !bookForm.value.publication_date) {
          throw new Error(t('admin.fillRequiredFields'));
        }

        if (!editingBook.value && !bookForm.value.pdf_file) {
          throw new Error(t('admin.pdfFileRequired'));
        }

        const formData = new FormData();
        formData.append('title', bookForm.value.title);
        formData.append('description', bookForm.value.description);
        formData.append('publication_date', bookForm.value.publication_date);
        // Always include boolean fields, even when false
        formData.append('is_published', bookForm.value.is_published ? 'true' : 'false');
        formData.append('is_featured', bookForm.value.is_featured ? 'true' : 'false');
        if (bookForm.value.isbn) {
          formData.append('isbn', bookForm.value.isbn);
        }

        // Handle authors
        if (editingBook.value) {
          // For updates, use existing authors
          if (bookForm.value.authors && bookForm.value.authors.length > 0) {
            bookForm.value.authors.forEach(authorId => {
              formData.append('authors', authorId);
            });
          }
        } else {
          // For new books, create a default author
          try {
            const authorResponse = await api.createAuthor({ 
              name: t('admin.defaultAuthor'),
              bio: t('admin.defaultAuthorBio')
            });
            formData.append('authors', authorResponse.data.id);
          } catch (authorErr) {
            console.error('Failed to create author:', authorErr);
            throw new Error(t('admin.failedToCreateAuthor'));
          }
        }
        
        // Handle categories
        if (bookForm.value.categories && bookForm.value.categories.length > 0) {
          bookForm.value.categories.forEach(catId => {
            formData.append('categories', catId);
          });
        }

        if (bookForm.value.pdf_file) {
          formData.append('pdf_file', bookForm.value.pdf_file);
        }
        if (bookForm.value.cover_image) {
          formData.append('cover_image', bookForm.value.cover_image);
        }

        // Generate slug only for new books
        if (!editingBook.value) {
          const slug = bookForm.value.title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          formData.append('slug', slug);
        }

        // Log the form data
        console.log('Book form data:');
        console.log('Editing book:', editingBook.value);
        console.log('Form values:', bookForm.value);
        console.log('is_published value:', bookForm.value.is_published);
        console.log('is_published type:', typeof bookForm.value.is_published);
        console.log('is_featured value:', bookForm.value.is_featured);
        console.log('is_featured type:', typeof bookForm.value.is_featured);
        for (let [key, value] of formData.entries()) {
          console.log(`${key}:`, value, `(type: ${typeof value})`);
        }

        let response;
        if (editingBook.value) {
          response = await api.updateBook(editingBook.value.id, formData);
        } else {
          response = await api.createBook(formData);
        }
        console.log('Book save response:', response);

        showAddBookModal.value = false;
        // Reset form
        bookForm.value = {
          title: '',
          description: '',
          categories: [],
          authors: [],
          publication_date: new Date().toISOString().split('T')[0],
          isbn: '',
          is_published: false,
          is_featured: false,
          pdf_file: null,
          cover_image: null,
          existing_pdf_url: null,
          existing_cover_url: null
        };
        editingBook.value = null;
        await fetchBooks();
      } catch (err) {
        console.error('Failed to save book:', err);
        console.error('Error response data:', err.response?.data);
        console.error('Error response status:', err.response?.status);
        console.error('Error response headers:', err.response?.headers);
        error.value = err.response?.data?.detail || 
                     err.message ||
                     Object.values(err.response?.data || {})[0]?.[0] ||
                     t('admin.failedToSaveBook');
      } finally {
        saving.value = false;
      }
    };

    const deleteBook = async (bookId) => {
      if (!confirm(t('admin.confirmDeleteBook'))) return;
      
      try {
        await api.deleteBook(bookId);
        fetchBooks();
      } catch (err) {
        error.value = t('admin.failedToDeleteBook');
        console.error(err);
      }
    };

    // Category actions
    const editCategory = (category) => {
      editingCategory.value = category;
      categoryForm.value = { ...category };
      showAddCategoryModal.value = true;
    };

    const saveCategory = async () => {
      saving.value = true;
      error.value = null;
      
      try {
        let response;
        if (editingCategory.value) {
          response = await api.updateCategory(editingCategory.value.slug, categoryForm.value);
        } else {
          response = await api.createCategory(categoryForm.value);
        }
        console.log('Save category response:', response);

        showAddCategoryModal.value = false;
        // Reset the form
        categoryForm.value = {
          name: '',
          description: ''
        };
        editingCategory.value = null;
        // Fetch updated categories list
        await fetchCategories();
      } catch (err) {
        console.error('Failed to save category:', err);
        console.error('Error response:', err.response);
        error.value = err.response?.data?.detail || 
                     Object.values(err.response?.data || {})[0]?.[0] ||
                     t('admin.failedToSaveCategory');
      } finally {
        saving.value = false;
      }
    };

    const deleteCategory = async (categoryId) => {
      if (!confirm(t('admin.confirmDeleteCategory'))) return;
      
      try {
        const categoryToDelete = categories.value.find(c => c.id === categoryId);
        if (!categoryToDelete) {
          throw new Error('Category not found');
        }
        await api.deleteCategory(categoryToDelete.slug);
        fetchCategories();
      } catch (err) {
        console.error('Failed to delete category:', err);
        error.value = t('admin.failedToDeleteCategory');
      }
    };

    // Fetch books and ads on mount
    onMounted(async () => {
      console.log('AdminDashboardView mounted');
      try {
        await fetchCategories();
        await fetchBooks();
        await fetchAdvertisements();
      } catch (err) {
        console.error('Error during initial data fetch:', err);
      }
    });

    return {
      activeTab,
      loading,
      error,
      saving,
      books,
      categories,
      showAddBookModal,
      showAddCategoryModal,
      editingBook,
      editingCategory,
      bookForm,
      categoryForm,
      handleFileUpload,
      handleCoverUpload,
      getPdfFilename,
      editBook,
      saveBook,
      deleteBook,
      editCategory,
      saveCategory,
      deleteCategory,
      advertisements, validAdvertisements, showAddAdModal, editingAd, adForm, adLoading, adError, adSaving, adBooks,
      openAdModal, handleAdImageUpload, saveAd, deleteAd, updateAdField, fetchAdvertisements,
    };
  }
});
</script>

<style lang="scss" scoped>
.admin-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.admin-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.tab-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: 500;
  color: #6b7280;
  transition: all 0.2s ease;
  font-family: 'Vazirmatn', sans-serif;

  i {
    margin-right: 0.5rem;
  }

  &:hover {
    background-color: #f3f4f6;
  }

  &.active {
    background-color: var(--primary-color);
    color: white;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
  }
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  th, td {
    padding: 1rem;
    text-align: start;
    border-bottom: 1px solid #e5e7eb;
  }

  th {
    background-color: #f9fafb;
    font-weight: 600;
  }
}

.category-tag {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: #f3f4f6;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;

  &.published {
    background-color: #d1fae5;
    color: #065f46;
  }

  &.draft {
    background-color: #f3f4f6;
    color: #6b7280;
  }
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.icon-btn {
  padding: 0.5rem;
  border: none;
  background: none;
  border-radius: 0.375rem;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
  }

  &.delete:hover {
    color: #ef4444;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.modal-content {
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }
}

.form-group {
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  input[type="text"],
  input[type="file"],
  textarea,
  select {
    width: 100%;
    padding: 0.625rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
    }
  }

      .existing-file {
      margin-top: 0.5rem;
      padding: 0.75rem;
      background-color: #f9fafb;
      border-radius: 0.375rem;
      border: 1px solid #e5e7eb;

      p {
        margin: 0 0 0.5rem 0;
        font-size: 0.875rem;
      }

      .file-info {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin: 0.5rem 0;

        .filename {
          font-family: 'Courier New', monospace;
          background-color: #e5e7eb;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.75rem;
          color: #374151;
        }
      }

      .file-link {
        color: var(--primary-color);
        text-decoration: none;
        font-weight: 500;

        &:hover {
          text-decoration: underline;
        }
      }

      .existing-cover {
        max-width: 200px;
        max-height: 150px;
        border-radius: 0.25rem;
        border: 1px solid #e5e7eb;
      }
    }

  select[multiple] {
    height: 120px;
  }

  &.checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    label {
      margin-bottom: 0;
      cursor: pointer;
    }
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.error-message {
  padding: 1rem;
  background-color: #fee2e2;
  border-radius: 0.5rem;
  color: #991b1b;
  margin-bottom: 1rem;
}
</style> 